import { defineStore } from "pinia";
import { watch } from "vue";
import type { notificationType } from "@/types/notification";
import { getListNotification, markAllAsRead, updateNotification } from "@/api/apiclient/notification";
import { useUser } from "./user";

/** Cadenza con cui avanzano le etichette "x minuti fa": solo un ricalcolo, nessuna chiamata */
const TIME_AGO_TICK_MS = 60 * 1000;

/** Polling di fallback mentre il socket è scollegato: la cadenza del vecchio auto-refresh */
const FALLBACK_POLL_MS = 2 * 60 * 1000;

/** Riallineamento lento a socket collegato: recupera eventi persi e letture fatte altrove */
const SAFETY_RESYNC_MS = 15 * 60 * 1000;

/** Watcher creati una sola volta: NotificationMenu può montarsi più volte (desktop e mobile) */
let watchersReady = false;

export const useNotification = defineStore("notification", {
  state: () => ({
    notifications: {
      unreads: [] as notificationType[],
      reads: [] as notificationType[],
    },
    tickTimer: null as number | null,
    refreshTimer: null as number | null,
    /** Stato del canale socket, comunicato da AppSocketListener: decide la cadenza del polling */
    isSocketConnected: false,
    tick: 0, // Forza il refresh delle date 'time ago'
    /** A GET of the list is in flight (first load, socket resync or an explicit refresh). */
    loading: false,
  }),
  getters: {
    unreadCount: (state): number => state.notifications.unreads.length,
    readCount: (state): number => state.notifications.reads.length,
  },
  actions: {
    /**
     * Reloads the whole list from the server. The previous items stay on screen until the new
     * ones arrive; on failure they are kept and the error is rethrown to the caller.
     */
    async init(): Promise<void> {
      this.loading = true;
      try {
        const response = await getListNotification({ page: 1, limit: 1000 });
        this.notifications.unreads = response.data.filter((n) => !n.read);
        this.notifications.reads = response.data.filter((n) => n.read);
        this.tick++; // forza ricalcolo
      } finally {
        this.loading = false;
      }
    },
    /** Avvia (o riavvia) la rilettura periodica con la cadenza adatta allo stato del socket */
    startAutoRefresh(): void {
      this.stopAutoRefresh();
      const interval = this.isSocketConnected ? SAFETY_RESYNC_MS : FALLBACK_POLL_MS;
      this.refreshTimer = window.setInterval(() => {
        this.init().catch((error) => console.error("[notification] auto refresh failed", error));
      }, interval);
    },

    stopAutoRefresh(): void {
      if (!this.refreshTimer) return;
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    },

    /** Il socket cambia stato: il polling, se attivo (utente loggato), riparte con la nuova cadenza */
    setSocketConnected(connected: boolean): void {
      if (this.isSocketConnected === connected) return;
      this.isSocketConnected = connected;
      if (this.refreshTimer) this.startAutoRefresh();
    },

    startTimeAgoTick(): void {
      if (this.tickTimer) return;
      this.tickTimer = window.setInterval(() => {
        this.tick++;
      }, TIME_AGO_TICK_MS);
    },

    stopTimeAgoTick(): void {
      if (!this.tickTimer) return;
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    },

    /**
     * Al login carica la lista; da lì in poi la rilegge AppSocketListener a ogni evento socket
     * "notification". Il polling resta come fallback (socket giù) e come riallineamento lento
     * (socket su).
     * Idempotente: i watcher si creano una volta sola anche se il menu si monta più volte.
     */
    setupAuthWatcher() {
      if (watchersReady) return;
      watchersReady = true;

      const userStore = useUser();

      watch(
        () => userStore.isLoggedIn,
        async (loggedIn) => {
          if (loggedIn) {
            this.startTimeAgoTick();
            this.startAutoRefresh();
            await this.init();
          } else {
            this.stopTimeAgoTick();
            this.stopAutoRefresh();
          }
        },
        { immediate: true }
      );
    },

    async updateNotification(id: number | string, read: boolean) {
      // Salva lo stato attuale (rollback se serve)
      const prevUnreads = [...this.notifications.unreads];
      const prevReads = [...this.notifications.reads];

      try {
        // Aggiorna subito lo store (optimistic update)
        if (read) {
          const notification = this.notifications.unreads.find((n) => n.id === id);
          if (notification) {
            const updated = { ...notification, read: true };
            this.notifications.unreads = this.notifications.unreads.filter((n) => n.id !== id);
            this.notifications.reads.unshift(updated);
          }
        } else {
          const notification = this.notifications.reads.find((n) => n.id === id);
          if (notification) {
            const updated = { ...notification, read: false };
            this.notifications.reads = this.notifications.reads.filter((n) => n.id !== id);
            this.notifications.unreads.unshift(updated);
          }
        }
        await updateNotification(id, read);
      } catch (err) {
        // Rollback se errore
        this.notifications.unreads = prevUnreads;
        this.notifications.reads = prevReads;
        throw err;
      }
    },
    async markAllAsRead() {
      // Salva stato precedente (per rollback)
      const prevUnreads = [...this.notifications.unreads];
      const prevReads = [...this.notifications.reads];

      try {
        // Aggiorna subito lo store (optimistic)
        const updated = this.notifications.unreads.map((n) => ({
          ...n,
          read: true,
        }));

        this.notifications.reads = [...updated, ...this.notifications.reads];
        this.notifications.unreads = [];

        await markAllAsRead();
      } catch (err) {
        // Rollback
        this.notifications.unreads = prevUnreads;
        this.notifications.reads = prevReads;
        throw err;
      }
    },
  },
});
